import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import MegaMenu from "@/components/MegaMenu";
import IloveMeIcon from "@/components/IloveMeIcon";
import SmartLink, { isExternal } from "@/components/SmartLink";
import { NAV_LINKS, MEGA_MENU, SIGNIN_OPTIONS } from "@/lib/site";

const SIGNIN_MENU_ID = "nav-signin-menu-panel";

const MEGA_MENU_ID = "nav-mega-menu-panel";
const MEGA_MENU_PATHS = MEGA_MENU.map((section) => section.to);
// Pages whose hero is light, so the transparent header needs dark text on them.
const LIGHT_HERO_PATHS = ["/sobre"];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const [signinOpen, setSigninOpen] = useState(false);
    const closeTimer = useRef(null);
    const { pathname } = useLocation();
    const darkText = scrolled || LIGHT_HERO_PATHS.includes(pathname);

    const openMega = () => {
        clearTimeout(closeTimer.current);
        setSigninOpen(false);
        setMegaOpen(true);
    };
    const scheduleCloseMega = () => {
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setMegaOpen(false), 150);
    };
    const closeMega = () => {
        clearTimeout(closeTimer.current);
        setMegaOpen(false);
    };
    const openSignin = () => {
        clearTimeout(closeTimer.current);
        setMegaOpen(false);
        setSigninOpen(true);
    };
    const scheduleCloseSignin = () => {
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setSigninOpen(false), 150);
    };

    const linkClass = ({ isActive }) =>
        `relative text-sm font-medium transition-colors duration-200 ${
            darkText
                ? isActive
                    ? "text-[#0047BB]"
                    : "text-[#08104F]/75 hover:text-[#08104F]"
                : isActive
                  ? "text-[#B2D0FF]"
                  : "text-[#F3EDE6]/85 hover:text-white"
        }`;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setMegaOpen(false);
        setSigninOpen(false);
    }, [pathname]);

    useEffect(() => {
        if (!signinOpen) return undefined;
        const onKey = (e) => e.key === "Escape" && setSigninOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [signinOpen]);

    useEffect(() => {
        if (!megaOpen) return undefined;
        const onKey = (e) => e.key === "Escape" && setMegaOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [megaOpen]);

    useEffect(() => () => clearTimeout(closeTimer.current), []);

    return (
        <header
            data-testid="nav-main-header"
            onMouseLeave={scheduleCloseMega}
            onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) closeMega();
            }}
            className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
                scrolled
                    ? "border-[#08104F]/10 bg-[#F3EDE6]/85 py-3 shadow-[0_8px_30px_rgba(8,16,79,0.06)] backdrop-blur-xl"
                    : "border-transparent bg-transparent py-5"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
                <Link to="/" data-testid="nav-link-home" aria-label="Contabeleza - Início" onMouseEnter={scheduleCloseMega}>
                    <Logo  dark={!darkText}  variant={darkText ? "blue" : "white"} className="h-6 w-auto sm:h-7" />
                </Link>

                <nav className="hidden items-center gap-8 lg:flex" aria-label="Navegação principal">
                    {NAV_LINKS.map((link) => {
                        const hasMega = MEGA_MENU_PATHS.includes(link.to);
                        return (
                            <div
                                key={link.to}
                                className="flex items-center gap-1"
                                onMouseEnter={hasMega ? openMega : scheduleCloseMega}
                            >
                                {isExternal(link.to) ? (
                                    <SmartLink to={link.to} data-testid={link.testId} className={linkClass({ isActive: false })}>
                                        {link.label === "ilove.me" ? <IloveMeIcon className="inline-flex items-center gap-1" /> : link.label}
                                    </SmartLink>
                                ) : (
                                    <NavLink to={link.to} data-testid={link.testId} className={linkClass}>
                                        {link.label}
                                    </NavLink>
                                )}
                                {hasMega && (
                                    <button
                                        type="button"
                                        aria-label={`Ver opções de ${link.label}`}
                                        aria-expanded={megaOpen}
                                        aria-controls={MEGA_MENU_ID}
                                        onClick={() => (megaOpen ? closeMega() : openMega())}
                                        className={`rounded-full p-0.5 transition-colors duration-200 ${
                                            darkText
                                                ? "text-[#08104F]/60 hover:text-[#08104F]"
                                                : "text-[#F3EDE6]/70 hover:text-white"
                                        }`}
                                    >
                                        <ChevronDown
                                            className={`h-3.5 w-3.5 transition-transform duration-200 ${
                                                megaOpen ? "rotate-180" : ""
                                            }`}
                                        />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-3" onMouseEnter={scheduleCloseMega}>
                    <div
                        className="relative hidden lg:block"
                        onMouseEnter={openSignin}
                        onMouseLeave={scheduleCloseSignin}
                    >
                        <button
                            type="button"
                            data-testid="btn-signin-dropdown"
                            aria-label="Entrar nas plataformas"
                            aria-expanded={signinOpen}
                            aria-controls={SIGNIN_MENU_ID}
                            onClick={() => (signinOpen ? setSigninOpen(false) : openSignin())}
                            className={`group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.97] ${
                                darkText
                                    ? "border border-[#08104F]/15 bg-[#FFFDFA]/70 text-[#08104F] hover:border-[#0047BB]/40 hover:text-[#0047BB]"
                                    : "border border-white/25 bg-white/10 text-[#F3EDE6] hover:bg-white/20 hover:text-white"
                            }`}
                        >
                            Entrar
                            <ChevronDown
                                className={`h-4 w-4 transition-transform duration-200 ${signinOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        <AnimatePresence>
                            {signinOpen && (
                                <motion.div
                                    id={SIGNIN_MENU_ID}
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                                    className="absolute right-0 top-full pt-3"
                                >
                                    <div className="w-64 overflow-hidden rounded-2xl border border-[#08104F]/10 bg-[#FFFDFA] p-2 shadow-[0_30px_80px_rgba(8,16,79,0.18)]">
                                        <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0047BB]">
                                            Entrar
                                        </p>
                                        {SIGNIN_OPTIONS.map((option) => (
                                            <a
                                                key={option.href}
                                                href={option.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={option.testId}
                                                className="-mx-1 flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-[15px] font-medium text-[#08104F] transition-colors duration-200 hover:bg-[#B2D0FF]/20 hover:text-[#0047BB]"
                                            >
                                                <span>
                                                    {option.label}
                                                    <span className="block text-xs font-normal text-[#3A4768]">
                                                        {option.description}
                                                    </span>
                                                </span>
                                                <ArrowUpRight className="h-4 w-4 shrink-0 opacity-60" />
                                            </a>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <Link
                        to="/contato"
                        data-testid="btn-header-cta-diagnostic"
                        className={`group hidden items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.97] sm:inline-flex ${
                            darkText
                                ? "bg-[#08104F] text-white hover:bg-[#0047BB]"
                                : "bg-[#F3EDE6] text-[#08104F] hover:bg-white"
                        }`}
                    >
                        Falar com especialista
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                    <button
                        type="button"
                        data-testid="btn-mobile-menu-toggle"
                        aria-label={open ? "Fechar menu" : "Abrir menu"}
                        onClick={() => setOpen((v) => !v)}
                        className={`inline-flex h-10 w-10 items-center justify-center rounded-full border lg:hidden ${
                            darkText
                                ? "border-[#08104F]/15 bg-[#FFFDFA]/70 text-[#08104F]"
                                : "border-white/25 bg-white/10 text-[#F3EDE6]"
                        }`}
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {megaOpen && (
                    <div className="hidden lg:block" onMouseEnter={openMega}>
                        <MegaMenu id={MEGA_MENU_ID} onNavigate={closeMega} />
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        className="mx-4 mt-3 max-h-[calc(100vh-6rem)] overflow-y-auto rounded-2xl border border-[#08104F]/10 bg-[#FFFDFA] p-4 shadow-xl lg:hidden"
                        aria-label="Navegação móvel"
                        data-lenis-prevent
                    >
                        {NAV_LINKS.map((link) => {
                            const section = MEGA_MENU.find((s) => s.to === link.to);
                            return (
                                <div key={link.to}>
                                    {isExternal(link.to) ? (
                                        <SmartLink
                                            to={link.to}
                                            data-testid={`mobile-${link.testId}`}
                                            onClick={() => setOpen(false)}
                                            className="block rounded-xl px-4 py-3 text-base font-medium text-[#08104F]"
                                        >
                                            {link.label === "ilove.me" ? <IloveMeIcon className="inline-flex items-center gap-1" /> : link.label}
                                        </SmartLink>
                                    ) : (
                                        <NavLink
                                            to={link.to}
                                            data-testid={`mobile-${link.testId}`}
                                            className={({ isActive }) =>
                                                `block rounded-xl px-4 py-3 text-base font-medium ${
                                                    isActive ? "bg-[#B2D0FF]/30 text-[#0047BB]" : "text-[#08104F]"
                                                }`
                                            }
                                        >
                                            {link.label}
                                        </NavLink>
                                    )}
                                    {section && (
                                        <ul className="mb-2 ml-4 border-l border-[#08104F]/10 pl-3">
                                            {section.items.map((item) => (
                                                <li key={item.to}>
                                                    <SmartLink
                                                        to={item.to}
                                                        onClick={() => setOpen(false)}
                                                        className="block rounded-lg px-3 py-2 text-sm text-[#3A4768] hover:text-[#0047BB]"
                                                    >
                                                        {item.label}
                                                    </SmartLink>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                        <div className="mt-2 border-t border-[#08104F]/10 pt-3">
                            <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0047BB]">
                                Entrar
                            </p>
                            {SIGNIN_OPTIONS.map((option) => (
                                <a
                                    key={option.href}
                                    href={option.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`mobile-${option.testId}`}
                                    onClick={() => setOpen(false)}
                                    className="flex items-center justify-between gap-2 rounded-xl px-4 py-3 text-base font-medium text-[#08104F] hover:bg-[#B2D0FF]/20 hover:text-[#0047BB]"
                                >
                                    {option.label}
                                    <ArrowUpRight className="h-4 w-4 opacity-60" />
                                </a>
                            ))}
                        </div>
                        <Link
                            to="/contato"
                            data-testid="mobile-btn-header-cta-diagnostic"
                            className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-[#08104F] px-4 py-3.5 text-base font-medium text-white"
                        >
                            Falar com especialista
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
